// material/run — the side view's interpreter: the module's ONE impure file.
// Performs each Effect against the ports and feeds the outcome back as a Msg.
// It owns the open live feeds (one per root), so `watch_job` is idempotent and
// `unwatch_job` closes exactly what was opened.

import { exhaustSwitchCase, Result } from "@bilda/utils";
import type {
    GenerationJobSnapshot,
    GenerationPorts,
    PortResult,
    ResourcePorts,
} from "../../app/ports.js";
import type { Interpreter } from "../module.js";
import type { MaterialEffect } from "./effect.js";
import type { JobIntent, ReviewIntent } from "./model.js";
import type { MaterialMsg, PinIntent } from "./msg.js";

export type MaterialDeps = GenerationPorts &
    Pick<ResourcePorts, "loadMaterial">;

export const mkRunMaterial = (
    deps: MaterialDeps,
): Interpreter<MaterialEffect, MaterialMsg> => {
    const feeds = new Map<string, () => void>();

    const answer = async (
        root: string,
        intent: JobIntent,
        call: PortResult<GenerationJobSnapshot>,
        dispatch: (msg: MaterialMsg) => void,
    ): Promise<void> => {
        const r = await call;
        dispatch(
            Result.isOk(r)
                ? { type: "intent_answered", root, intent, snapshot: r.value }
                : r.error._tag === "External.Refused" &&
                    r.error.errorType === "generation_root_ungrounded"
                  ? { type: "start_refused_ungrounded", root }
                  : { type: "intent_failed", root, intent },
        );
    };

    const loadContent = async (
        root: string,
        part: string,
        key: string,
        dispatch: (msg: MaterialMsg) => void,
    ): Promise<void> => {
        const r = await deps.loadMaterial(part);
        dispatch(
            Result.isOk(r)
                ? {
                      type: "part_content_loaded",
                      root,
                      key,
                      content: {
                          elements: r.value.elements,
                          provenance: r.value.provenance,
                      },
                  }
                : { type: "part_content_failed", root, key },
        );
    };

    // Every changed dependency re-pinned, or the answer fails as a whole (a
    // retry re-pins only what is still behind: the server re-reads).
    const stillFine = async (
        root: string,
        part: string,
        dependencies: readonly string[],
        dispatch: (msg: MaterialMsg) => void,
    ): Promise<void> => {
        const rs = await Promise.all(
            dependencies.map((d) => deps.markBuildsOnStillFine(part, d)),
        );
        dispatch(
            rs.every(Result.isOk)
                ? { type: "still_fine_answered", root, part }
                : { type: "still_fine_failed", root, part },
        );
    };

    const answerNotice = async (
        root: string,
        part: string,
        answer: PortResult<void>,
        intent: PinIntent,
        dispatch: (msg: MaterialMsg) => void,
    ): Promise<void> =>
        dispatch(
            Result.isOk(await answer)
                ? { type: "pin_answered", root, part }
                : { type: "pin_answer_failed", root, intent },
        );
    const review = async (
        root: string,
        intent: ReviewIntent,
        call: PortResult<void>,
        dispatch: (msg: MaterialMsg) => void,
    ): Promise<void> => {
        const r = await call;
        dispatch(
            Result.isOk(r)
                ? { type: "review_answered", root, intent }
                : { type: "review_failed", root, intent },
        );
    };

    return (effect, dispatch) => {
        switch (effect.type) {
            case "load_skeleton": {
                const { root } = effect;
                void deps.loadGenerationSkeleton(root).then((r) =>
                    dispatch(
                        Result.isOk(r)
                            ? {
                                  type: "skeleton_loaded",
                                  root,
                                  skeleton: r.value,
                              }
                            : { type: "skeleton_failed", root },
                    ),
                );
                return;
            }
            case "load_job": {
                const { root } = effect;
                void deps.showGenerationJob(root).then((r) =>
                    dispatch(
                        Result.isOk(r)
                            ? {
                                  type: "job_loaded",
                                  root,
                                  snapshot: r.value,
                              }
                            : { type: "job_failed", root },
                    ),
                );
                return;
            }
            case "watch_job": {
                const { root } = effect;
                if (feeds.has(root)) return;
                const close = deps.watchGenerationJob(root, (event) => {
                    switch (event.type) {
                        case "generation_feed_opened":
                            return dispatch({ type: "feed_restored", root });
                        case "generation_feed_lost":
                            return dispatch({ type: "feed_lost", root });
                        case "generation_feed_snapshot":
                            return dispatch({
                                type: "feed_snapshot",
                                root,
                                snapshot: event.snapshot,
                            });
                        default:
                            return exhaustSwitchCase(event);
                    }
                });
                feeds.set(root, close);
                return;
            }
            case "unwatch_job": {
                feeds.get(effect.root)?.();
                feeds.delete(effect.root);
                return;
            }
            case "start_job":
                void answer(
                    effect.root,
                    { _tag: "Intent.Start" },
                    deps.startGeneration(effect.root, effect.parts),
                    dispatch,
                );
                return;
            case "stop_job":
                void answer(
                    effect.root,
                    { _tag: "Intent.Stop" },
                    deps.stopGeneration(effect.root),
                    dispatch,
                );
                return;
            case "continue_job":
                void answer(
                    effect.root,
                    { _tag: "Intent.Continue" },
                    deps.continueGeneration(effect.root),
                    dispatch,
                );
                return;
            case "revise_part":
                void answer(
                    effect.root,
                    { _tag: "Intent.Revise", part: effect.part },
                    deps.reviseGenerationPart(
                        effect.root,
                        effect.part,
                        effect.feedback,
                    ),
                    dispatch,
                );
                return;
            case "load_part_content":
                void loadContent(
                    effect.root,
                    effect.part,
                    effect.key,
                    dispatch,
                );
                return;
            case "mark_still_fine":
                void stillFine(
                    effect.root,
                    effect.part,
                    effect.dependencies,
                    dispatch,
                );
                return;
            case "use_newer_version":
                void answerNotice(
                    effect.root,
                    effect.part,
                    deps.advanceReference(
                        effect.root,
                        effect.refId,
                        effect.toVersion,
                    ),
                    { _tag: "Intent.UseVersion", part: effect.part },
                    dispatch,
                );
                return;
            case "keep_version":
                void answerNotice(
                    effect.root,
                    effect.part,
                    deps.declineNewerVersions(effect.root, effect.refId),
                    { _tag: "Intent.KeepVersion", part: effect.part },
                    dispatch,
                );
                return;
            case "approve_part":
                void review(
                    effect.root,
                    { _tag: "Intent.Approve", part: effect.part },
                    deps.approvePart(effect.part, effect.version),
                    dispatch,
                );
                return;
            case "revoke_part_approval":
                void review(
                    effect.root,
                    { _tag: "Intent.RevokeApproval", part: effect.part },
                    deps.revokePartApproval(effect.part, effect.version),
                    dispatch,
                );
                return;
            default:
                return exhaustSwitchCase(effect);
        }
    };
};
